/**
 * 🚀 localStorage 缓存层
 * 减少重复的 localStorage 读取操作，提升性能
 * 
 * 规则参考: js-cache-storage (Vercel React Best Practices)
 */

// 内存缓存
const cache = new Map();

/**
 * 从 localStorage 读取值（带缓存）
 * @param {string} key - 存储键名
 * @returns {string|null} - 存储的值
 */
export function getLocalStorage(key) {
  if (cache.has(key)) {
    return cache.get(key);
  }
  
  try {
    const value = localStorage.getItem(key);
    cache.set(key, value);
    return value;
  } catch (e) {
    // 处理隐私模式或存储禁用的情况
    console.warn('localStorage read failed:', e);
    return null;
  }
}

/**
 * 向 localStorage 写入值（同步更新缓存）
 * @param {string} key - 存储键名
 * @param {string} value - 要存储的值
 */
export function setLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
    cache.set(key, value);
  } catch (e) {
    console.warn('localStorage write failed:', e);
  }
}

/**
 * 从 localStorage 删除值（同步更新缓存）
 * @param {string} key - 存储键名
 */
export function removeLocalStorage(key) {
  try {
    localStorage.removeItem(key);
    cache.delete(key);
  } catch (e) {
    console.warn('localStorage remove failed:', e);
  }
}

/**
 * 获取并解析 JSON 值（带缓存）
 * @param {string} key - 存储键名
 * @param {*} defaultValue - 解析失败时的默认值
 * @returns {*} - 解析后的对象
 */
export function getLocalStorageJSON(key, defaultValue = null) {
  const value = getLocalStorage(key);
  if (value === null) return defaultValue;
  
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn('JSON parse failed for key:', key, e);
    return defaultValue;
  }
}

/**
 * 将对象序列化并存储到 localStorage（同步更新缓存）
 * @param {string} key - 存储键名
 * @param {*} value - 要存储的对象
 */
export function setLocalStorageJSON(key, value) {
  try {
    const serialized = JSON.stringify(value);
    setLocalStorage(key, serialized);
  } catch (e) {
    console.warn('JSON stringify failed for key:', key, e);
  }
}

/**
 * 清除指定键的缓存（用于外部更新后同步）
 * @param {string} key - 存储键名
 */
export function invalidateCache(key) {
  cache.delete(key);
}

/**
 * 清除所有缓存
 */
export function clearCache() {
  cache.clear();
}

// 监听其他标签页的 storage 变化，自动失效缓存
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key) {
      cache.delete(e.key);
    } else {
      // 如果 key 为 null，表示 localStorage.clear() 被调用
      cache.clear();
    }
  });
  
  // 页面重新可见时清除缓存，确保数据最新
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      cache.clear();
    }
  });
}
